<template>
  <div>
    <CShelf class="u-mb-1" align="bottom">
      <template #left>
        <CLabel class="u-mb-2">{{ label }}</CLabel>
      </template>
      <template v-if="collapsable && !forceExpanded" #right>
        <CButton theme="ghost-primary" @click="expanded = !expanded">
          {{ expanded ? $t('contactSelector.collapse') : $t('contactSelector.expand', { count: context.contacts.length }) }}
          <CDirectionIcon :direction="expanded ? 'up' : 'down'"/>
        </CButton>
      </template>
    </CShelf>

    <template v-if="expanded">
      <!-- empty state is shown by adding null option even if required - should be blocked by validation -->
      <CField type="radio" v-model="model" :options="context.contacts" :nullable="!required || context.contacts.length === 0">
        <template #nullOption>
          <span class="u-semibold">{{ emptyLabel || $t('contactSelector.empty') }}</span>
        </template>

        <template #option="{option}">
          <CContactSelectorItem :contact="option" :context/>
        </template>
      </CField>

      <CButton theme="normal-primary" @click="ContactModals.contact(context)">
        <CIcon name="plus"/>
        {{ $t('contactSelector.create') }}
      </CButton>
    </template>

    <CCard v-else>
      <CContactSelectorItem v-if="selectedContact" :contact="selectedContact" :context collapsed/>
      <span v-else class="u-semibold">{{ emptyLabel || $t('contactSelector.empty') }}</span>
    </CCard>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref, watchEffect } from 'vue';
  import CContactSelectorItem from '@/contact/components/CContactSelectorItem.vue';
  import ContactContext from '@/contact/models/ContactContext';
  import DossierContact from '@/contact/models/DossierContact';
  import ContactModals from '@/contact/util/ContactModals';
  import CCard from '../CCard.vue';
  import CDirectionIcon from '../CDirectionIcon.vue';
  import CIcon from '../CIcon.vue';
  import CShelf from '../CShelf.vue';
  import CButton from '../buttons/CButton.vue';
  import CLabel from './CLabel.vue';
  import CField from './CField.vue';

  const props = withDefaults(defineProps<{
    label: string;
    context: ContactContext;
    emptyLabel?: string;
    required?: boolean;
    collapsable?: boolean;
  }>(), {
    collapsable: true,
  });

  const model = defineModel();

  const selectedContact = computed<DossierContact | null | undefined>(() => {
    const originalContact = model.value;
    if (originalContact) return props.context.contacts.find(c => c.id === originalContact.id);
  });

  // under some conditions collapse makes little sense because the user will have to edit anyhow
  // forcing expand in these cases also greatly simplifies the code (only 1 way to show error & empty state etc)
  const forceExpanded = computed(() => props.required && !selectedContact.value);

  const expanded = ref(!props.collapsable);

  watchEffect(() => {
    if (forceExpanded.value) expanded.value = true;
  });

  watchEffect(() => {
    if (selectedContact.value === undefined) model.value = null;
  });
</script>